import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Link from '@cloudscape-design/components/link';
import ExpandableSection from '@cloudscape-design/components/expandable-section';

export const AboutMailHeaderTab = () => {
  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">What are Email Headers?</Header>}>
        <SpaceBetween size="m">
          <Box variant="p">
            Email headers are metadata attached to every email message. They contain information about the sender, 
            recipient, routing, and security features. Headers help email servers deliver messages correctly and 
            allow users to diagnose delivery issues, verify authenticity, and understand the technical anatomy of 
            an email.
          </Box>
          <Box variant="p">
            Email headers are structured according to the Internet Message Format defined in{' '}
            <Link href="https://www.rfc-editor.org/rfc/rfc5322" external>RFC 5322</Link>. Each header consists 
            of a field name followed by a colon and the field value. Headers are added by each mail server that 
            handles the message, creating a complete audit trail of the email's journey.
          </Box>
          <Box variant="p">
            <strong>Purpose of email headers:</strong>
          </Box>
          <SpaceBetween size="xs">
            <Box variant="p">• Verify sender identity and prevent spoofing</Box>
            <Box variant="p">• Trace the complete delivery path</Box>
            <Box variant="p">• Detect tampering and modifications</Box>
            <Box variant="p">• Troubleshoot delivery problems</Box>
            <Box variant="p">• Identify spam and phishing attempts</Box>
          </SpaceBetween>
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Email Authentication</Header>}>
        <SpaceBetween size="m">
          <Box variant="h3">SPF (Sender Policy Framework)</Box>
          <Box variant="p">
            SPF allows domain owners to specify which mail servers are authorized to send email on their
            behalf. When an email is received, the receiving server checks if the sending server's IP address
            is listed in the sender domain's SPF record.
          </Box>
          <Box variant="p">
            <strong>Status meanings:</strong>
          </Box>
          <Box variant="p">
            • <strong>Pass:</strong> The sending server is authorized to send email for this domain
            <br />
            • <strong>Fail:</strong> The sending server is NOT authorized (potential spoofing)
            <br />• <strong>None:</strong> No SPF record found for the domain
          </Box>

          <Box variant="h3">DKIM (DomainKeys Identified Mail)</Box>
          <Box variant="p">
            DKIM adds a digital signature to email messages, allowing receivers to verify that the email
            hasn't been tampered with during transit and that it actually came from the claimed domain. The
            signature is validated using a public key published in the domain's DNS records.
          </Box>
          <Box variant="p">
            <strong>Status meanings:</strong>
          </Box>
          <Box variant="p">
            • <strong>Pass:</strong> Signature is valid and message is unmodified
            <br />
            • <strong>Fail:</strong> Signature is invalid or message was altered
            <br />• <strong>None:</strong> No DKIM signature found
          </Box>

          <Box variant="h3">DMARC (Domain-based Message Authentication)</Box>
          <Box variant="p">
            DMARC builds on SPF and DKIM by adding a policy layer that tells receiving servers what to do
            with emails that fail authentication checks. Domain owners can specify whether to quarantine,
            reject, or accept messages that don't pass SPF or DKIM validation.
          </Box>
          <Box variant="p">
            <strong>Policy options:</strong>
          </Box>
          <Box variant="p">
            • <strong>none:</strong> Monitor only, don't take action
            <br />
            • <strong>quarantine:</strong> Move suspicious emails to spam
            <br />• <strong>reject:</strong> Block emails that fail authentication
          </Box>

          <Box variant="h3">ARC (Authenticated Received Chain)</Box>
          <Box variant="p">
            ARC preserves authentication results when emails are forwarded or modified by intermediate services
            (like mailing lists or forwarding services). It creates a chain of trust by allowing each intermediate
            server to sign the authentication results from the previous hop, preventing legitimate emails from
            being rejected due to forwarding.
          </Box>
          <Box variant="p">
            <strong>ARC headers:</strong>
          </Box>
          <Box variant="p">
            • <strong>ARC-Authentication-Results:</strong> Authentication results from each hop
            <br />
            • <strong>ARC-Seal:</strong> Seal that validates the ARC chain
            <br />• <strong>ARC-Message-Signature:</strong> Cryptographic signature for the message at each hop
          </Box>
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">How to Extract Email Headers</Header>}>
        <SpaceBetween size="m">
          <ExpandableSection headerText="Gmail" defaultExpanded>
            <SpaceBetween size="s">
              <Box variant="p">1. Open the email message</Box>
              <Box variant="p">2. Click the three dots (More) icon in the top right</Box>
              <Box variant="p">3. Select "Show original"</Box>
              <Box variant="p">4. Copy the entire content or download the .eml file</Box>
            </SpaceBetween>
          </ExpandableSection>

          <ExpandableSection headerText="Outlook / Microsoft 365">
            <SpaceBetween size="s">
              <Box variant="p">1. Open the email message</Box>
              <Box variant="p">2. Go to File → Properties (desktop) or click the three dots → View message details (web)</Box>
              <Box variant="p">3. Copy the content from the "Internet headers" field</Box>
            </SpaceBetween>
          </ExpandableSection>

          <ExpandableSection headerText="Apple Mail">
            <SpaceBetween size="s">
              <Box variant="p">1. Open the email message</Box>
              <Box variant="p">2. Go to View → Message → All Headers</Box>
              <Box variant="p">3. Select and copy the header content</Box>
            </SpaceBetween>
          </ExpandableSection>

          <ExpandableSection headerText="Yahoo Mail">
            <SpaceBetween size="s">
              <Box variant="p">1. Open the email message</Box>
              <Box variant="p">2. Click the three dots (More) icon</Box>
              <Box variant="p">3. Select "View raw message"</Box>
              <Box variant="p">4. Copy the entire content</Box>
            </SpaceBetween>
          </ExpandableSection>
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Understanding Relay Paths</Header>}>
        <SpaceBetween size="m">
          <Box variant="p">
            The relay path shows each mail server the email passed through on its journey from sender to
            recipient. Each "hop" in the path is recorded in a "Received:" header, with the most recent hop
            appearing first.
          </Box>

          <Box variant="h3">Reading a Relay Hop</Box>
          <div
            style={{
              fontFamily: 'monospace',
              fontSize: '12px',
              padding: '12px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
              wordBreak: 'break-all',
            }}
          >
            Received: from mail.sender.com (192.168.1.1)
            <br />
            by mail.recipient.com (10.0.0.1)
            <br />
            with ESMTPS id ABC123
            <br />; Thu, 16 Oct 2025 11:27:26 +0000
          </div>

          <SpaceBetween size="s">
            <Box variant="p">
              • <strong>from:</strong> The sending server's hostname and IP address
            </Box>
            <Box variant="p">
              • <strong>by:</strong> The receiving server that created this header
            </Box>
            <Box variant="p">
              • <strong>with:</strong> The protocol used (SMTP, ESMTPS, etc.)
            </Box>
            <Box variant="p">
              • <strong>Timestamp:</strong> When this server received the message
            </Box>
          </SpaceBetween>

          <Box variant="h3">Delivery Delays</Box>
          <Box variant="p">
            The time difference between consecutive "Received:" headers shows how long the email spent at
            each hop. Normal delays are usually just a few seconds, but longer delays might indicate network
            issues, spam filtering, or server problems.
          </Box>
          <Box variant="p">
            <strong>Typical delay ranges:</strong>
          </Box>
          <Box variant="p">
            • <strong>{"<1 second"}:</strong> Normal, fast delivery
            <br />
            • <strong>1-10 seconds:</strong> Normal, may include spam scanning
            <br />
            • <strong>10-60 seconds:</strong> Possibly slower network or filtering
            <br />• <strong>{">1 minute"}:</strong> Unusual, may indicate problems or heavy filtering
          </Box>
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Security Implications</Header>}>
        <SpaceBetween size="m">
          <Box variant="h3">Why Headers Matter for Security</Box>
          <Box variant="p">
            Email headers are crucial for identifying phishing attempts, spoofed emails, and other security
            threats. By analyzing headers, you can verify:
          </Box>
          <SpaceBetween size="s">
            <Box variant="p">
              • Whether the email actually came from the claimed sender (SPF/DKIM/DMARC)
            </Box>
            <Box variant="p">
              • If the email was modified during transit (DKIM signature validation)
            </Box>
            <Box variant="p">
              • The true origin of the email (sender IP address)
            </Box>
            <Box variant="p">
              • Whether the email passed through suspicious servers
            </Box>
            <Box variant="p">
              • If there are mismatches between displayed and actual sender addresses
            </Box>
          </SpaceBetween>

          <Box variant="h3">Red Flags to Watch For</Box>
          <SpaceBetween size="s">
            <Box variant="p">
              • <strong>Failed authentication:</strong> SPF, DKIM, or DMARC failures suggest spoofing
            </Box>
            <Box variant="p">
              • <strong>Mismatched domains:</strong> "From" domain differs from "Return-Path" domain
            </Box>
            <Box variant="p">
              • <strong>Suspicious servers:</strong> Email routed through unexpected countries or providers
            </Box>
            <Box variant="p">
              • <strong>Missing headers:</strong> Legitimate emails always have complete header chains
            </Box>
            <Box variant="p">
              • <strong>Forged headers:</strong> Inconsistent or impossible timestamps
            </Box>
          </SpaceBetween>
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Common Header Fields</Header>}>
        <SpaceBetween size="m">
          <ExpandableSection headerText="Basic Headers" defaultExpanded>
            <SpaceBetween size="s">
              <Box variant="p">
                • <strong>From:</strong> The email address of the sender
              </Box>
              <Box variant="p">
                • <strong>To:</strong> The primary recipient(s) of the email
              </Box>
              <Box variant="p">
                • <strong>Cc:</strong> Carbon Copy - secondary recipients
              </Box>
              <Box variant="p">
                • <strong>Bcc:</strong> Blind Carbon Copy - hidden recipients (not visible in received headers)
              </Box>
              <Box variant="p">
                • <strong>Subject:</strong> The subject line of the message
              </Box>
              <Box variant="p">
                • <strong>Date:</strong> The date and time when the email was sent
              </Box>
              <Box variant="p">
                • <strong>Message-ID:</strong> A unique identifier for the email message
              </Box>
              <Box variant="p">
                • <strong>Reply-To:</strong> The email address to which replies should be sent
              </Box>
              <Box variant="p">
                • <strong>In-Reply-To:</strong> References a parent email's Message-ID for threading
              </Box>
              <Box variant="p">
                • <strong>References:</strong> Lists Message-IDs of related emails in a thread
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          <ExpandableSection headerText="Routing & Delivery Headers">
            <SpaceBetween size="s">
              <Box variant="p">
                • <strong>Received:</strong> List of servers that handled the message, added in reverse order
              </Box>
              <Box variant="p">
                • <strong>Return-Path:</strong> The email address where bounce messages are sent
              </Box>
              <Box variant="p">
                • <strong>Delivered-To:</strong> The final recipient address used by the mail server
              </Box>
              <Box variant="p">
                • <strong>X-Originating-IP:</strong> The IP address of the original sender
              </Box>
              <Box variant="p">
                • <strong>X-Mailer:</strong> Identifies the email client or software used to send the message
              </Box>
              <Box variant="p">
                • <strong>X-Priority:</strong> Indicates the priority level (1=highest, 5=lowest)
              </Box>
              <Box variant="p">
                • <strong>X-Forwarded-For:</strong> Original IP when behind a proxy or load balancer
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          <ExpandableSection headerText="Content & MIME Headers">
            <SpaceBetween size="s">
              <Box variant="p">
                • <strong>MIME-Version:</strong> Specifies the MIME version (usually 1.0)
              </Box>
              <Box variant="p">
                • <strong>Content-Type:</strong> Describes the media type (text/plain, text/html, etc.)
              </Box>
              <Box variant="p">
                • <strong>Content-Transfer-Encoding:</strong> How the email body is encoded (quoted-printable, base64, etc.)
              </Box>
              <Box variant="p">
                • <strong>X-MS-Has-Attach:</strong> Indicates the message contains attachments
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          <ExpandableSection headerText="Authentication & Security Headers">
            <SpaceBetween size="s">
              <Box variant="p">
                • <strong>Authentication-Results:</strong> Results of SPF, DKIM, and DMARC checks
              </Box>
              <Box variant="p">
                • <strong>DKIM-Signature:</strong> Digital signature to verify sender domain and detect tampering
              </Box>
              <Box variant="p">
                • <strong>Received-SPF:</strong> SPF validation details
              </Box>
              <Box variant="p">
                • <strong>DMARC-Filter:</strong> Applied DMARC policy or processing status
              </Box>
              <Box variant="p">
                • <strong>ARC-Seal:</strong> Authenticated Received Chain seal for forwarding integrity
              </Box>
              <Box variant="p">
                • <strong>ARC-Message-Signature:</strong> ARC signature for intermediary servers
              </Box>
              <Box variant="p">
                • <strong>ARC-Authentication-Results:</strong> Authentication results preserved across forwarding
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          <ExpandableSection headerText="Anti-Spam & Filtering Headers">
            <SpaceBetween size="s">
              <Box variant="p">
                • <strong>X-Spam-Status:</strong> Result of spam scan (Yes/No) with metadata
              </Box>
              <Box variant="p">
                • <strong>X-Spam-Score:</strong> Numeric score indicating spam likelihood
              </Box>
              <Box variant="p">
                • <strong>X-Microsoft-Antispam:</strong> Microsoft's spam classification codes
              </Box>
              <Box variant="p">
                • <strong>X-MS-Exchange-Organization-SCL:</strong> Spam Confidence Level (0-9, higher = more likely spam)
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          <ExpandableSection headerText="Mailing List Headers">
            <SpaceBetween size="s">
              <Box variant="p">
                • <strong>List-ID:</strong> Uniquely identifies the mailing list
              </Box>
              <Box variant="p">
                • <strong>List-Unsubscribe:</strong> Provides method to unsubscribe (URL or mailto link)
              </Box>
              <Box variant="p">
                • <strong>Precedence:</strong> Used for bulk/auto-replies (bulk, list, junk)
              </Box>
              <Box variant="p">
                • <strong>X-Auto-Response-Suppress:</strong> Prevents automatic responses (OOF, AutoReply)
              </Box>
            </SpaceBetween>
          </ExpandableSection>
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Frequently Asked Questions</Header>}>
        <SpaceBetween size="m">
          <ExpandableSection headerText="What is the purpose of email headers?" defaultExpanded>
            <Box variant="p">
              Email headers contain metadata about the email, including sender, recipient, routing information, 
              and security details like DKIM and SPF. They help verify authenticity, trace delivery paths, and 
              troubleshoot issues.
            </Box>
          </ExpandableSection>

          <ExpandableSection headerText="How do I view raw email headers?">
            <Box variant="p">
              Most email clients allow you to view raw headers by opening the email, accessing the menu or options, 
              and selecting "View Headers", "All Headers", or "Show Original". The exact location varies by client 
              but is typically found in the More menu or File properties.
            </Box>
          </ExpandableSection>

          <ExpandableSection headerText="What is DKIM in email headers?">
            <Box variant="p">
              DomainKeys Identified Mail (DKIM) is an email authentication method that uses a digital signature 
              to verify the sender's domain and detect tampering. The signature is validated using a public key 
              published in the domain's DNS records.
            </Box>
          </ExpandableSection>

          <ExpandableSection headerText="Why is SPF important in email headers?">
            <Box variant="p">
              Sender Policy Framework (SPF) helps prevent email spoofing by verifying that the sender's IP address 
              is authorized to send emails for the domain. It's a critical first line of defense against 
              impersonation attacks.
            </Box>
          </ExpandableSection>

          <ExpandableSection headerText="Can email headers be forged?">
            <Box variant="p">
              Some parts of the email header, such as the "From" field, can be spoofed by attackers. However, 
              fields added by trusted mail servers (like "Received" headers, "Authentication-Results", and DKIM 
              signatures) are much more difficult to forge. This is why authentication mechanisms like SPF, DKIM, 
              and DMARC are so important.
            </Box>
          </ExpandableSection>

          <ExpandableSection headerText="What does 'Received' header tell me?">
            <Box variant="p">
              The "Received" header shows each mail server the email passed through on its journey. Each hop adds 
              a new "Received" header with the server names, IP addresses, protocol used, and timestamp. Reading 
              these in reverse order (bottom to top) shows the complete path from sender to recipient.
            </Box>
          </ExpandableSection>
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Additional Resources</Header>}>
        <SpaceBetween size="s">
          <Box variant="p">Learn more about email headers and security:</Box>
          <Box variant="p">
            •{' '}
            <Link href="https://www.ditig.com/introduction-to-email-headers" external>
              Introduction to Email Headers - Comprehensive Guide
            </Link>
          </Box>
          <Box variant="p">
            •{' '}
            <Link href="https://www.rfc-editor.org/rfc/rfc5322" external>
              RFC 5322 - Internet Message Format
            </Link>
          </Box>
          <Box variant="p">
            •{' '}
            <Link href="https://www.rfc-editor.org/rfc/rfc5321" external>
              RFC 5321 - Simple Mail Transfer Protocol (SMTP)
            </Link>
          </Box>
          <Box variant="p">
            •{' '}
            <Link href="https://www.rfc-editor.org/rfc/rfc7208" external>
              RFC 7208 - Sender Policy Framework (SPF)
            </Link>
          </Box>
          <Box variant="p">
            •{' '}
            <Link href="https://www.rfc-editor.org/rfc/rfc6376" external>
              RFC 6376 - DomainKeys Identified Mail (DKIM)
            </Link>
          </Box>
          <Box variant="p">
            •{' '}
            <Link href="https://www.rfc-editor.org/rfc/rfc7489" external>
              RFC 7489 - Domain-based Message Authentication (DMARC)
            </Link>
          </Box>
          <Box variant="p">
            •{' '}
            <Link href="https://dkim.org/" external>
              DKIM.org - Official DKIM Resources
            </Link>
          </Box>
          <Box variant="p">
            •{' '}
            <Link href="http://www.openspf.org/" external>
              OpenSPF - Sender Policy Framework
            </Link>
          </Box>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};

